import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MdDashboard, MdPeople, MdContacts, MdSecurity, MdNotifications, MdAssessment, MdQuestionAnswer } from 'react-icons/md';
import { FaUserPlus, FaUsers, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getDatabase, ref, onValue } from 'firebase/database';
import { app } from '../firebase';

const Sidebar = ({ sidebarOpen, setSidebarOpen, collapsed, setCollapsed }) => {
    // const [collapsed, setCollapsed] = useState(false); // Controlled by parent now
    const location = useLocation();
    const { employeeData } = useAuth();
    const [pendingEnquiriesCount, setPendingEnquiriesCount] = useState(0);

    // Fetch pending enquiries count
    useEffect(() => {
        const db = getDatabase(app);
        const enquiriesRef = ref(db, 'enquiries');

        const unsubscribe = onValue(enquiriesRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const pendingCount = Object.values(data).filter(enquiry => {
                    const status = enquiry.status ? enquiry.status.toLowerCase() : 'pending';
                    return status === 'pending' || status === 'followup' || (!['complete', 'completed', 'closed'].includes(status));
                }).length;
                setPendingEnquiriesCount(pendingCount);
            } else {
                setPendingEnquiriesCount(0);
            }
        });

        return () => unsubscribe();
    }, []);

    // Map permission keys to paths
    const permissionMap = {
        '/dashboard': 'dashboardAccess',
        '/add-customer': 'addCustomerAccess',
        '/customer-list': 'customerListAccess',
        '/insurance': 'insurenceAccess',
        '/contacts': 'dealersAccess',
        '/employees': 'employeesAccess',
        '/reports': 'reportsAccess',
        '/reminders': 'remindersAccess',
        '/enquiries': 'enquiriesAccess'
    };

    // Check if user is an employee/agent
    const isEmployee = employeeData &&
        employeeData.role &&
        (employeeData.role.toLowerCase() === 'agent' || employeeData.role.toLowerCase() === 'employee');

    const allMenuItems = [
        {
            icon: MdDashboard,
            label: 'Dashboard',
            // Show employee dashboard for employees, admin dashboard for admins
            path: isEmployee ? '/emp-dashboard' : '/dashboard'
        },
        {
            icon: FaUserPlus,
            label: 'Add Customer',
            path: '/add-customer'
        },
        {
            icon: FaUsers,
            label: 'Customer List',
            path: '/customer-list'
        },
        {
            icon: MdPeople,
            label: 'Employees',
            path: '/employees'
        },
        {
            icon: MdContacts,
            label: 'Contacts',
            path: '/contacts'
        },
        {
            icon: MdSecurity,
            label: 'Insurance',
            path: '/insurance'
        },
        {
            icon: MdNotifications,
            label: 'Reminders',
            path: '/reminders'
        },
        {
            icon: MdAssessment,
            label: 'Reports',
            path: '/reports'
        },
        {
            icon: MdQuestionAnswer,
            label: 'Enquiries',
            path: '/enquiries'
        }
    ];

    // Filter menu items based on employee permissions
    const menuItems = (employeeData && employeeData.role !== 'Manager')
        ? allMenuItems.filter(item => {
            // For employees, always show the employee dashboard
            if (item.path === '/emp-dashboard') return true;

            // specific check for admins or if permissions missing completely
            if (!employeeData.permissions) return false;

            const permKey = permissionMap[item.path];
            // If path not in map (like dealers), default to false for employees
            if (!permKey) return false;

            return employeeData.permissions[permKey] === true;
        })
        : allMenuItems; // Show all if no permission data (admin) or if Manager

    const isActive = (path) => {
        // Exact match for the path
        if (location.pathname === path) return true;

        // Check for sub-routes
        // For /employees, also match /add-employee and /employee/:id/edit
        if (path === '/employees' && (location.pathname === '/add-employee' || location.pathname.startsWith('/employee/'))) {
            return true;
        }

        // For /contacts, also match /add-contact and /edit-contact/:id
        if (path === '/contacts' && (location.pathname === '/add-contact' || location.pathname.startsWith('/edit-contact/'))) {
            return true;
        }

        // For /customer-list, also match /customer/:id/edit
        if (path === '/customer-list' && location.pathname.startsWith('/customer/')) {
            return true;
        }

        // For /insurance, also match /insurance/:id/edit
        if (path === '/insurance' && location.pathname.startsWith('/insurance/')) {
            return true;
        }

        return false;
    };

    return (
        <div className={`${collapsed ? 'w-14' : 'w-48'
            } h-screen flex flex-col transition-all duration-300 shadow-lg fixed left-0 top-0 z-40 
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
            style={{ backgroundColor: '#F5F7FA' }}>

            {/* Logo/Header - At the very top, same height as navbar */}
            <div className="h-16 flex items-center px-4 border-b bg-white" style={{ borderColor: '#E2E8F0' }}>
                <div className="flex items-center justify-between w-full">
                    {!collapsed && (
                        <div className="flex items-center space-x-2">
                            <img
                                src="/logo.png"
                                alt="Vardhaman Financial Services"
                                className="w-8 h-8 rounded-full object-cover border-2 shadow-lg"
                                style={{ borderColor: '#2E865F' }}
                            />
                            <div>
                                <h2 className="text-gray-800 font-bold text-sm">Vardhaman</h2>
                                <p className="text-gray-800 text-sm font-bold">Finance</p>
                            </div>
                        </div>
                    )}
                    {collapsed && (
                        <div className="flex justify-center w-full">
                            <img
                                src="/logo.png"
                                alt="Vardhaman"
                                className="w-8 h-8 rounded-full object-cover border-2"
                                style={{ borderColor: '#2E865F' }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Menu */}
            <nav className="flex-1 py-4 px-2 overflow-y-auto">
                <div className="space-y-2">
                    {menuItems.map((item) => {
                        const IconComponent = item.icon;
                        const showBadge = item.path === '/enquiries' && pendingEnquiriesCount > 0;

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen && setSidebarOpen(false)}
                                className={`relative flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive(item.path)
                                    ? 'shadow-md'
                                    : 'text-gray-600 hover:bg-gray-200/50'
                                    }`}
                                style={isActive(item.path) ? { backgroundColor: '#2E865F', color: 'white' } : {}}
                            >
                                <div className="flex items-center space-x-2">
                                    <IconComponent className="text-xl flex-shrink-0" />
                                    {!collapsed && (
                                        <span className="font-semibold text-sm">{item.label}</span>
                                    )}
                                </div>
                                {showBadge && !collapsed && (
                                    <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                                        {pendingEnquiriesCount > 9 ? '9+' : pendingEnquiriesCount}
                                    </span>
                                )}
                                {showBadge && collapsed && (
                                    <span className="absolute top-1 right-1 bg-red-500 rounded-full h-2 w-2 animate-pulse"></span>
                                )}
                            </Link>
                        );
                    })}
                </div>
            </nav>

            {/* Toggle Button - at the bottom of sidebar */}
            <div className="p-3 border-t hidden lg:block" style={{ borderColor: '#E2E8F0' }}>
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className={`w-full flex items-center justify-center px-3 py-2.5 rounded-xl transition-all duration-200 hover:shadow-md ${collapsed ? 'bg-gray-200 hover:bg-gray-300' : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                    style={{ backgroundColor: '#2E865F', color: 'white' }}
                    title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                >
                    {collapsed ? (
                        <FaChevronRight className="text-lg" />
                    ) : (
                        <FaChevronLeft className="text-lg" />
                    )}
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
